// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useState } from "react";
import { createRoot } from "react-dom/client";
import { toast } from "sonner";
import { CodeHighlight } from "@/components/shared/code-highlight";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface ApiErrorDetails {
  method: string;
  url: string;
  status: number;
  body: string;
  requestBody?: string;
}

function ApiErrorDialog({
  details,
  open,
  onOpenChange,
}: {
  details: ApiErrorDetails;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const hasStatus = details.status > 0;
  const statusColor = "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>API Error</DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            {hasStatus && (
              <Badge variant="secondary" className={statusColor}>
                {details.status}
              </Badge>
            )}
            <span className="font-mono text-xs">
              {details.method} {new URL(details.url).pathname}
            </span>
          </DialogDescription>
        </DialogHeader>
        {details.requestBody && (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">Request</p>
            <CodeHighlight value={details.requestBody} lang="json" className="max-h-[200px]" />
          </div>
        )}
        {hasStatus && (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">Response</p>
            <CodeHighlight value={details.body} lang="json" className="max-h-[400px]" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function mountDialog(details: ApiErrorDetails) {
  const container = document.createElement("div");
  document.body.appendChild(container);
  const root = createRoot(container);

  function Wrapper() {
    const [open, setOpen] = useState(true);
    return (
      <ApiErrorDialog
        details={details}
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) {
            setTimeout(() => {
              root.unmount();
              container.remove();
            }, 300);
          }
        }}
      />
    );
  }

  root.render(<Wrapper />);
}

export function showApiErrorToast(details: ApiErrorDetails) {
  const pathname = new URL(details.url).pathname;
  const description =
    details.status > 0
      ? `${details.method} ${pathname} — ${details.status}`
      : `${details.method} ${pathname} — Network error`;

  toast.error("API request failed", {
    description,
    action: {
      label: "Details",
      onClick: () => mountDialog(details),
    },
  });
}
